import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { useI18n } from '../i18n';
import { Icon } from './icon';
import { cssVars } from './css-vars';
import { fadeIn } from './motion';
import { useCompact } from './index';

// Material's list-detail layout (TF-98, canonical layouts): on a window 840 wide or more, once a record
// is chosen the list moves into a pane at the start and the record opens in a second pane beside it;
// the address is the record's, so links, back and forward work. With nothing chosen the list keeps the
// whole width - its table, its columns, its selection. On a narrower window it is one page at a time,
// as before: the record replaces the list, and back returns to the list where it was - same scroll,
// same filters (the list stays mounted, hidden, while the record shows).
//
// The divider between the panes is dragged (or moved with the arrow keys) and the width remembered;
// a button gives the record the whole width. F6 moves between the panes; in the list, the up and down
// arrows move from record to record and the second pane follows.

/**
 * Which pane a page is drawn in: the list pane, the record pane, a side sheet (a deal from the board),
 * or none (a page of its own). Inside one, a page leaves out what the panes draw once for both (the
 * CRM tabs) and what the place already says (the breadcrumbs back to the list).
 */
export type Pane = 'list' | 'detail' | 'sheet' | null;
const PaneContext = createContext<Pane>(null);
export const PaneProvider = PaneContext.Provider;
export const usePane = (): Pane => useContext(PaneContext);

/**
 * A list's title: the page's heading on its own, the list pane's (h2) beside a record - the record's
 * title is then the page's one h1, for a screen reader's outline and for search.
 */
export function PageTitle({ children }: { children: ReactNode }) {
  return useContext(PaneContext) === 'list' ? <h2 className="pane-title">{children}</h2> : <h1>{children}</h1>;
}

/** Whether a list should be the narrow kind (a Material list rather than a table): a phone, or a list pane. */
export function useNarrow(): boolean {
  const compact = useCompact();
  return useContext(PaneContext) === 'list' || compact;
}

const EXPANDED = '(min-width: 840px)';

/** The list-detail section an address is in ("/c/x/crm/contacts" for a contact), or null. */
export function listDetailSection(pathname: string): string | null {
  return /^(\/c\/[^/]+\/crm\/(?:contacts|accounts|deals))(?:\/[^/]+)?\/?$/.exec(pathname)?.[1] ?? null;
}

/** Whether a move between two addresses stays in the same pair of panes: the page around does not change. */
export function staysInPanes(from: string, to: string): boolean {
  const section = listDetailSection(from);
  return section !== null && section === listDetailSection(to) && typeof window !== 'undefined' && window.matchMedia(EXPANDED).matches;
}

/** Whether the window is expanded or larger (840 and up): room for two panes. */
export function useExpanded(): boolean {
  const [expanded, setExpanded] = useState(() => typeof window !== 'undefined' && window.matchMedia(EXPANDED).matches);
  useEffect(() => {
    const media = window.matchMedia(EXPANDED);
    const change = () => setExpanded(media.matches);
    media.addEventListener('change', change);
    return () => media.removeEventListener('change', change);
  }, []);
  return expanded;
}

const WIDTH_KEY = 'teamfolio.listPaneWidth';
const FULL_KEY = 'teamfolio.detailFullWidth';
export const LIST_WIDTH = { min: 320, max: 560, initial: 380 } as const;

const clampWidth = (width: number) => Math.min(LIST_WIDTH.max, Math.max(LIST_WIDTH.min, Math.round(width)));

function remembered(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function remember(key: string, value: string | null) {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // not remembered; still works
  }
}

export interface TwoPaneProps {
  /** The list, with its filters: the first pane. */
  list: ReactNode;
  /** The record chosen, or null when none is. */
  detail: ReactNode | null;
  /** Which record it is (its address): another one starts at its top. */
  detailKey?: string;
  /** The list pane's name for a screen reader ("Contacts"). */
  label: string;
  /** What in the list opens a record (a CSS selector): the up and down arrows move along these. */
  rows?: string;
  /** Above both panes, the width of both (a section's tabs). */
  head?: ReactNode;
  /** One page at a time on a wide window too: the record as a page, back to the list (TF-367). */
  onePage?: boolean;
}

export function TwoPane(props: TwoPaneProps) {
  const expanded = useExpanded();
  if (!expanded || props.onePage) return <OnePage list={props.list} detail={props.detail} />;
  // Nothing chosen: the list as its own page, the whole width (it draws its own head).
  if (props.detail === null) return <>{props.list}</>;
  return <Panes {...props} />;
}

/**
 * A list row's link to a record, keeping the list's filters and order in the address (they are the
 * list's own search parameters): the list beside the record stays as it was, and back returns to it.
 */
export function useRecordLink(): (path: string) => string {
  const { search } = useLocation();
  return (path) => `${path}${search}`;
}

/**
 * One page at a time (compact and medium windows). The list stays mounted while a record shows, so
 * its filters, what it loaded and its place survive; the window's scroll is put back on return.
 */
function OnePage({ list, detail }: { list: ReactNode; detail: ReactNode | null }) {
  const showing = detail !== null;
  const listScroll = useRef(0);
  const was = useRef(showing);
  useLayoutEffect(() => {
    if (showing === was.current) return;
    was.current = showing;
    if (showing) {
      // listScroll was kept while the list showed (below); the record starts at its top.
      window.scrollTo(0, 0);
    } else {
      window.scrollTo(0, listScroll.current);
    }
  }, [showing]);
  useEffect(() => {
    if (showing) return;
    const keep = () => {
      listScroll.current = window.scrollY;
    };
    window.addEventListener('scroll', keep, { passive: true });
    return () => window.removeEventListener('scroll', keep);
  }, [showing]);
  return (
    <>
      <div className="ld-one" hidden={showing}>
        {list}
      </div>
      {detail}
    </>
  );
}

function Panes({ list, detail, detailKey, label, rows, head }: TwoPaneProps) {
  const { t } = useI18n();
  const [width, setWidth] = useState(() => clampWidth(Number(remembered(WIDTH_KEY)) || LIST_WIDTH.initial));
  const [full, setFull] = useState(() => remembered(FULL_KEY) === 'yes');
  const listRef = useRef<HTMLElement>(null);
  const detailRef = useRef<HTMLElement>(null);
  const drag = useRef<{ x: number; width: number } | null>(null);

  const resize = (next: number) => {
    const clamped = clampWidth(next);
    setWidth(clamped);
    remember(WIDTH_KEY, String(clamped));
  };
  const toggleFull = () => {
    setFull(!full);
    remember(FULL_KEY, full ? null : 'yes');
  };

  // Another record starts at its top, and fades in; the list does not move.
  useLayoutEffect(() => {
    const pane = detailRef.current;
    pane?.scrollTo?.(0, 0);
    fadeIn(pane);
  }, [detailKey]);

  // F6 and Shift+F6: from pane to pane, as in a desktop program.
  const panes = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'F6') return;
    event.preventDefault();
    const inList = listRef.current?.contains(document.activeElement);
    const target = full ? detailRef.current : inList ? detailRef.current : listRef.current;
    target?.focus();
  };

  // In the list, up and down move from record to record; the record pane follows.
  const along = (event: KeyboardEvent<HTMLElement>) => {
    if ((event.key !== 'ArrowDown' && event.key !== 'ArrowUp') || !rows || event.altKey || event.ctrlKey || event.metaKey) return;
    const links = [...(listRef.current?.querySelectorAll<HTMLElement>(rows) ?? [])];
    // Only from a row: arrows keep their meaning in a field or a menu.
    const at = links.indexOf(document.activeElement as HTMLElement);
    if (links.length === 0 || at === -1) return;
    event.preventDefault();
    const next = links[Math.min(links.length - 1, Math.max(0, at + (event.key === 'ArrowDown' ? 1 : -1)))]!;
    next.focus();
    next.click();
  };

  const startDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    drag.current = { x: event.clientX, width };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const moveDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (drag.current) setWidth(clampWidth(drag.current.width + event.clientX - drag.current.x));
  };
  const endDrag = () => {
    if (!drag.current) return;
    drag.current = null;
    remember(WIDTH_KEY, String(width));
  };

  return (
    <>
    {head}
    <div className="ld" data-full={full || undefined} ref={cssVars({ '--ld-list': `${width}px` })} onKeyDown={panes}>
      <PaneProvider value="list">
        <section ref={listRef} className="ld-pane ld-list" aria-label={label} tabIndex={-1} hidden={full} onKeyDown={along}>
          {list}
        </section>
      </PaneProvider>
      {!full && (
        <div
          className="ld-divider"
          role="separator"
          aria-orientation="vertical"
          aria-label={t('panes.divider')}
          aria-valuemin={LIST_WIDTH.min}
          aria-valuemax={LIST_WIDTH.max}
          aria-valuenow={width}
          tabIndex={0}
          onPointerDown={startDrag}
          onPointerMove={moveDrag}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onDoubleClick={() => resize(LIST_WIDTH.initial)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowLeft') resize(width - 16);
            else if (event.key === 'ArrowRight') resize(width + 16);
            else if (event.key === 'Home') resize(LIST_WIDTH.min);
            else if (event.key === 'End') resize(LIST_WIDTH.max);
            else return;
            event.preventDefault();
          }}
        />
      )}
      <section ref={detailRef} className="ld-pane ld-detail" tabIndex={-1}>
        <div className="ld-detail-bar">
          <button type="button" className="icon-btn" aria-label={full ? t('panes.showList') : t('panes.fullWidth')} aria-pressed={full} onClick={toggleFull}>
            <Icon name={full ? 'left_panel_open' : 'left_panel_close'} />
          </button>
        </div>
        <PaneProvider value="detail">{detail}</PaneProvider>
      </section>
    </div>
    </>
  );
}
